import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import uuid from 'uuid';
import classnames from 'classnames';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  USER_TEXT_TRACKING_LENGTH,
  LOADING_TIMEOUT,
  ROLE,
  SELECTION_TYPE,
  PREDEFINED_MESSAGE,
  ERROR_CREATIVE_ID,
  STREAM_STATE,
} from 'site-modules/canvas/constants/canvas';
import { ellipsisTruncate } from 'client/utils/string-utils';
import { fireNoStreamIdTracking } from 'site-modules/canvas/utils/canvas-tracking';
import { ElementScrollChatMessages } from 'site-modules/canvas/components/chat-messages/element-scroll-chat-messages';
import { ChatInput } from 'site-modules/canvas/components/chat-input/chat-input';
import { ScrollDownArrow } from 'site-modules/canvas/components/scroll-down-arrow/scroll-down-arrow';
import { getQuery } from 'client/utils/location';
import { venomHistory } from 'client/utils/history/venom-history';
import { removeQueryParam } from 'site-modules/shared/utils/url-utils';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { StartScreenSuggestedMessages } from 'site-modules/canvas/components/start-screen-suggested-messages/start-screen-suggested-messages';
import { StartScreenSuggestedMessages as StartScreenSuggestedMessagesDesktop } from 'site-modules/canvas/components/start-screen-suggested-messages/start-screen-suggested-messages-desktop';
import { isConciergePage } from 'site-modules/canvas/utils/is-concierge-page';

import './chat-canvas.scss';

export const ChatCanvas = memo(
  forwardRef(
    (
      {
        onLinkClick,
        onMCPLinkClick,
        iframeRef = null,
        llmClient,
        trackingParent = null,
        onChatProgressChange = noop,
        onLoadingChange = noop,
        inputClassName = undefined,
        chatMessagesClassName = undefined,
        MessagesRenderer = ElementScrollChatMessages,
        scrollArrowStrategy,
        inputContainerClassName = undefined,
        location = null,
        isMobile = false,
        isAuthenticated,
        startScreenHeader = null,
        onInitializationChange = noop,
      },
      ref
    ) => {
      const [isLoading, setLoading] = useState(false);
      const [userMessage, setUserMessage] = useState('');
      const [streamState, setStreamState] = useState(null);
      const [showSurvey, setShowSurvey] = useState(false);
      const [setHistoryTimeout, clearHistoryTimeout] = useTimeout();
      const messageParam = getQuery(location)?.message;
      const [messages, setMessages] = useState(null);
      const isHistoryLoading = messages === null;
      const [startMessage, setStartMessage] = useState(null);
      const [suggestedMessage, setSuggestedMessage] = useState(null);
      const currentResponseId = useRef(null);
      const inputRef = useRef();

      const isChatInProgress = !!messages?.length;

      useEffect(() => {
        onChatProgressChange?.(isChatInProgress);
      }, [isChatInProgress, onChatProgressChange]);

      useEffect(() => {
        onLoadingChange?.(isLoading);
      }, [isLoading, onLoadingChange]);

      useEffect(() => {
        onInitializationChange(isHistoryLoading);
      }, [isHistoryLoading, onInitializationChange]);

      const getResponsePromise = useCallback(
        (message, respId) => {
          return llmClient.current.askLlm(message, { respId }).then(id => {
            currentResponseId.current = id;
            return id;
          });
        },
        [llmClient]
      );

      const onAiMessageComplete = useCallback(
        async message => {
          const lastMessage = messages?.at(-1);

          if (
            isConciergePage(location) ||
            message.userText === PREDEFINED_MESSAGE.WELCOME ||
            message.historyResponseId ||
            lastMessage !== message
          ) {
            return;
          }

          const respId = uuid.v4();
          const parentResponseId = message.generatedResponseId;

          const timestamp = Date.now();
          const responsePromise = llmClient.current.askLlm(PREDEFINED_MESSAGE.SUGGESTION, { respId });

          setSuggestedMessage({
            id: `ai-${timestamp}`,
            role: ROLE.ASSISTANT,
            userText: PREDEFINED_MESSAGE.SUGGESTION,
            responsePromise,
            generatedResponseId: respId,
          });

          try {
            const id = await responsePromise;

            EventToolbox.fireTrackAction({
              event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
              event_data: {
                action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
                action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
                action_name: TrackingConstant.VIEW_SEARCH_RESULT,
                subaction_name: TrackingConstant.VIEW_AUTO_COMPLETE_RESULT,
                input: parentResponseId,
                creative_id: trackingParent,
                selection_type: SELECTION_TYPE.SUGGESTED_MESSAGE,
                value: `suggested_search_id=${id}`,
                resp_uuid: parentResponseId,
                autocomplete_results: id,
              },
            });
          } catch (error) {
            fireNoStreamIdTracking(error, ERROR_CREATIVE_ID.SUGGESTED, respId);
          }
        },
        [llmClient, location, messages, trackingParent]
      );

      const onSubmit = useCallback(
        (message, { selectionType, creativeId = trackingParent } = {}) => {
          setLoading(true);
          setSuggestedMessage(null);

          const respId = uuid.v4();

          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
            event_data: {
              action_category: TrackingConstant.USER_ACTION_CATEGORY,
              action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
              action_name: TrackingConstant.VIEW_SEARCH_RESULT,
              subaction_name: TrackingConstant.VIEW_AUTO_COMPLETE_RESULT,
              creative_id: creativeId,
              input: ellipsisTruncate(message, USER_TEXT_TRACKING_LENGTH),
              selection_type: selectionType,
              value:
                iframeRef?.current?.contentWindow?.EDM?.preloadedState?.pageContext?.legacy?.pageName ||
                iframeRef?.current?.contentWindow?.EDM?.preloadedState?.pageContext?.page?.name,
              resp_uuid: respId,
            },
          });

          const timestamp = Date.now();
          setMessages(prevMessages => [
            ...(prevMessages ?? []),
            { id: `${timestamp}`, role: ROLE.USER, text: message },
            {
              id: `ai-${timestamp}`,
              role: ROLE.ASSISTANT,
              userText: message,
              responsePromise: getResponsePromise(message, respId),
              generatedResponseId: respId,
            },
          ]);
        },
        [getResponsePromise, iframeRef, trackingParent]
      );

      const isStartScreen = isHistoryLoading || !messages.length;
      const isDesktopStartScreen = isStartScreen && !isMobile;
      const isMobileStartScreen = isStartScreen && isMobile;

      const setStartMessagePromise = useCallback(() => {
        const startRespId = uuid.v4();

        setStartMessage({
          id: `ai-${Date.now()}`,
          role: ROLE.ASSISTANT,
          userText: PREDEFINED_MESSAGE.WELCOME,
          responsePromise: getResponsePromise(PREDEFINED_MESSAGE.WELCOME, startRespId),
          generatedResponseId: startRespId,
        });
      }, [getResponsePromise]);

      useEffect(() => {
        setHistoryTimeout(() => {
          setMessages([]);
          llmClient.current.abort();
        }, LOADING_TIMEOUT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        llmClient.current.fetchMessageHistory().then(response => {
          clearHistoryTimeout();

          const startMessageFromHistory = response?.find(({ userText }) => userText === PREDEFINED_MESSAGE.WELCOME);
          const historyMessages = response?.filter(({ userText }) => userText !== PREDEFINED_MESSAGE.WELCOME);

          setMessages(historyMessages || []);

          if (messageParam) {
            onSubmit(decodeURIComponent(messageParam));
            venomHistory.replace(removeQueryParam(`${location?.pathname}${location?.search}`, 'message'));
          } else if (startMessageFromHistory) {
            setStartMessage(startMessageFromHistory);
          } else if (!response?.length) {
            setStartMessagePromise();
          }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        function onIframeFocus() {
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
            event_data: {
              action_category: TrackingConstant.USER_ACTION_CATEGORY,
              action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
              action_name: TrackingConstant.VIEW_SEARCH_RESULT,
              subaction_name: TrackingConstant.ABANDON_AUTO_COMPLETE_RESULT,
              creative_id: trackingParent,
              resp_uuid: currentResponseId.current,
            },
          });
        }

        const iframe = iframeRef?.current;
        function onIframeLoad() {
          iframe?.contentWindow?.addEventListener('focus', onIframeFocus);
        }

        iframe?.addEventListener('load', onIframeLoad);

        return () => {
          iframe?.contentWindow?.removeEventListener('focus', onIframeFocus);
          iframe?.removeEventListener('load', onIframeLoad);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const onCancel = useCallback(() => {
        llmClient.current.abort();
        setLoading(false);

        if (!messages) return;

        const [lastUserMessage, lastAiMessage] = messages.slice(-2);
        setUserMessage(lastUserMessage?.text);
        setMessages([...messages.slice(0, -1), { ...lastAiMessage, isCanceled: true }]);
      }, [llmClient, messages]);

      useImperativeHandle(
        ref,
        () => ({
          clearChat: () => {
            llmClient.current.abort();
            llmClient.current.clearHistory();
            currentResponseId.current = null;
            setLoading(false);
            setUserMessage('');
            setShowSurvey(false);
            setMessages([]);
            setStartMessagePromise();
            setSuggestedMessage(null);
          },
        }),
        [llmClient, setStartMessagePromise]
      );

      const StartScreenSuggestedMessagesComponent = isMobile
        ? StartScreenSuggestedMessages
        : StartScreenSuggestedMessagesDesktop;
      const chatInput = (
        <div
          className={classnames('pt-1 chat-input-transition', inputContainerClassName, {
            'chat-input-transition-active': isChatInProgress,
          })}
        >
          <ScrollDownArrow
            scrollArrowStrategy={scrollArrowStrategy}
            displayable={streamState === STREAM_STATE.STREAMING}
          />
          <ChatInput
            className={classnames('mt-auto', inputClassName)}
            userMessage={userMessage}
            onSubmit={onSubmit}
            isLoading={isLoading}
            isAuthenticated={isAuthenticated}
            onCancel={onCancel}
            inputRef={inputRef}
            withInfoText={!isStartScreen}
            isStartScreen={isStartScreen}
            isChatInProgress={isChatInProgress}
            showSurvey={showSurvey}
            onShowSurvey={setShowSurvey}
            isMobile={isMobile}
          />
        </div>
      );
      const startScreenSuggestedMessages = !isHistoryLoading && isStartScreen && (
        <StartScreenSuggestedMessagesComponent
          className={chatMessagesClassName}
          onSubmit={onSubmit}
          startMessage={startMessage}
        />
      );
      const content = (
        <>
          {isStartScreen && startScreenHeader ? (
            <div className="start-screen-content d-flex flex-column w-100">
              {startScreenHeader}
              {chatInput}
              {startScreenSuggestedMessages}
            </div>
          ) : (
            <>
              {chatInput}
              {startScreenSuggestedMessages}
            </>
          )}
          {isStartScreen && (
            <p
              className={classnames('small text-center mb-1 mt-auto', {
                'text-cool-gray-10': isMobileStartScreen,
                'text-cool-gray-40': isDesktopStartScreen,
              })}
            >
              Co-Driver AI can make mistakes. Check important info. Do not include personal or sensitive information.
            </p>
          )}
        </>
      );

      return (
        <>
          <div
            className={classnames('chat-container d-flex flex-column flex-fill', { 'chat-active': isChatInProgress })}
          >
            <MessagesRenderer
              className={classnames(chatMessagesClassName, {
                'd-none': isStartScreen,
              })}
              messages={messages}
              isChatInProgress={isChatInProgress}
              setLoading={setLoading}
              onLinkClick={onLinkClick}
              onMCPLinkClick={onMCPLinkClick}
              onAiMessageComplete={onAiMessageComplete}
              onStreamStateChange={setStreamState}
              onSubmit={onSubmit}
              inputRef={inputRef}
              suggestedMessage={suggestedMessage}
              isMobile={isMobile}
            />
            {content}
          </div>
          <Experiment name="search-658-mcp-rc" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal" />
          </Experiment>
        </>
      );
    }
  )
);

ChatCanvas.propTypes = {
  onLinkClick: PropTypes.func.isRequired,
  onMCPLinkClick: PropTypes.func.isRequired,
  llmClient: PropTypes.shape({ current: PropTypes.shape() }).isRequired,
  iframeRef: PropTypes.shape({ current: PropTypes.shape() }),
  trackingParent: PropTypes.string,
  onChatProgressChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  inputClassName: PropTypes.string,
  chatMessagesClassName: PropTypes.string,
  MessagesRenderer: PropTypes.elementType,
  scrollArrowStrategy: PropTypes.shape({
    shouldShowArrow: PropTypes.func.isRequired,
    scrollDown: PropTypes.func.isRequired,
    getScrollContainer: PropTypes.func.isRequired,
    getResizeObserverTarget: PropTypes.func.isRequired,
    attachScrollListener: PropTypes.func.isRequired,
    detachScrollListener: PropTypes.func.isRequired,
  }).isRequired,
  inputContainerClassName: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  startScreenHeader: PropTypes.node,
  onInitializationChange: PropTypes.func,
};
